import type { FeedComment } from '@/types/feed.type'

import { z } from 'zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import { Textarea } from '@/components/ui/textarea'
import { MEDIA_LIMIT } from '@/constant/media-limit'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import {
  Loader2,
  Image as LucideImage,
  Video as LucideVideo,
  X as LucideX,
} from 'lucide-react'

import type { commentSchema } from './feed-comment-section'

type CommentFormValues = z.infer<typeof commentSchema>

// Modal ฟอร์มคอมเมนต์
interface CommentFormDialogProps {
  form: ReturnType<typeof useForm<CommentFormValues>>
  onSubmit: (values: CommentFormValues) => Promise<void>
  isSubmitting: ReturnType<typeof useBoolean>
  cooldown: number
  mediaType: 'image' | 'video'
  setMediaType: (type: 'image' | 'video') => void
  fileInputRef: React.RefObject<HTMLInputElement>
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  mediaPreview: { image: string | null; video: string | null }
  replyData: FeedComment | null
  removeMedia: () => void
}

const CommentFormDialog: React.FC<CommentFormDialogProps> = ({
  form,
  onSubmit,
  isSubmitting,
  cooldown,
  mediaType,
  setMediaType,
  fileInputRef,
  handleFileChange,
  mediaPreview,
  replyData,
  removeMedia,
}) => (
  <DialogContent className="p-0 max-w-lg w-[95dvw]" aria-describedby="">
    <DialogHeader className="p-6 pb-0">
      <DialogTitle className="truncate w-[calc(100dvw-100px)] max-w-[455px]">
        {replyData
          ? `ตอบกลับ${replyData?.content ? ` ${replyData?.content}` : ''}`
          : 'แสดงความคิดเห็น'}
      </DialogTitle>
    </DialogHeader>
    <div className="p-6 pt-2">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Textarea
                      maxLength={500}
                      placeholder="พิมพ์ความคิดเห็นของคุณ..."
                      className="min-h-[100px] resize-none bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all break-all"
                      {...field}
                    />
                    <div className="absolute -bottom-[22px] right-3 text-xs text-gray-500">
                      {field.value?.length || 0}/500
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* Media Upload */}
          <div className="space-y-3">
            <Tabs
              defaultValue={mediaType}
              onValueChange={(val) => {
                setMediaType(val as 'image' | 'video')
                removeMedia()
              }}
              className="w-full"
            >
              <TabsList className="mb-2">
                <TabsTrigger value="image" className="flex items-center gap-2">
                  <LucideImage className="w-4 h-4" /> รูปภาพ
                </TabsTrigger>
                <TabsTrigger value="video" className="flex items-center gap-2">
                  <LucideVideo className="w-4 h-4" /> วิดีโอ
                </TabsTrigger>
              </TabsList>
              <TabsContent value="image">
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors bg-gray-50">
                    <Input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setMediaType('image')
                        fileInputRef.current?.click()
                      }}
                      className="flex flex-col items-center gap-2 w-full"
                    >
                      <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <LucideImage className="w-4 h-4 text-blue-500" />
                      </div>
                      <div className="text-gray-600">
                        <p className="text-sm font-medium">
                          เพิ่มรูปภาพ (ไม่เกิน{' '}
                          {MEDIA_LIMIT.MAX_IMAGE_SIZE / 1024 / 1024}MB)
                        </p>
                      </div>
                    </button>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="video">
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors bg-gray-50">
                    <Input
                      type="file"
                      accept="video/*"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setMediaType('video')
                        fileInputRef.current?.click()
                      }}
                      className="flex flex-col items-center gap-2 w-full"
                    >
                      <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <LucideVideo className="w-4 h-4 text-blue-500" />
                      </div>
                      <div className="text-gray-600">
                        <p className="text-sm font-medium">
                          เพิ่มวิดีโอ (ไม่เกิน{' '}
                          {MEDIA_LIMIT.MAX_VIDEO_SIZE / 1024 / 1024}MB)
                        </p>
                      </div>
                    </button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            {/* Media Preview */}
            {(mediaPreview.image || mediaPreview.video) && (
              <div className="relative">
                <div className="w-full h-32 rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={mediaPreview.image || mediaPreview.video || ''}
                    alt="media preview"
                    className="w-full h-full object-cover"
                  />
                  {mediaPreview.video && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="bg-gray-600/60 rounded-full p-2">
                        <LucideVideo className="w-6 h-6 text-white opacity-80" />
                      </div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={removeMedia}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center"
                >
                  <LucideX className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex space-x-2 text-gray-500"></div>
            <Button
              type="submit"
              disabled={isSubmitting.value || cooldown > 0}
              size="sm"
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {isSubmitting.value ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : cooldown > 0 ? (
                `รอ ${cooldown}s`
              ) : (
                'ส่งความคิดเห็น'
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  </DialogContent>
)

export default CommentFormDialog
